import { gameTimeMatcher } from "@/lib/game-date";
import { searchMatcher } from "@/lib/odds-screen/live/derive";
import type { ArbLeg, EventHeader, LowHoldRow, LowHoldView, RowId } from "@/lib/odds-screen/live/types";
import { displayOrder, marketLabel } from "@/lib/arbitrage/labels";
import { coverShares, holdOf, planStakes, type StakePlan } from "@/lib/arbitrage/plan";
import { hitsTarget, isHidden, matchesScope, matchesSearch } from "@/lib/arbitrage/rows";
import type { LowHoldFilters } from "./filters";

const EPSILON = 1e-9;

export type HoldPlay = {
  readonly row: LowHoldRow;
  readonly event: EventHeader;
  readonly market: string;
  readonly legs: readonly ArbLeg[];
  readonly hold: number;
  readonly plan: StakePlan;
  readonly cost: number;
};

export function selectedCovers(legs: readonly ArbLeg[], selected: ReadonlySet<string>): ArbLeg[][] {
  const sides: string[] = [];
  const options = new Map<string, ArbLeg[]>();
  for (const leg of legs) {
    if (!sides.includes(leg.side)) sides.push(leg.side);
    if (selected.has(leg.book) && (leg.limit === null || leg.limit >= 1)) options.set(leg.side, [...(options.get(leg.side) ?? []), leg]);
  }
  const covers: { readonly legs: ArbLeg[]; readonly sum: number }[] = [];
  const walk = (index: number, picked: ArbLeg[], sum: number) => {
    if (index === sides.length) {
      if (new Set(picked.map((leg) => leg.book)).size >= 2) covers.push({ legs: picked, sum });
      return;
    }
    for (const leg of options.get(sides[index]) ?? []) walk(index + 1, [...picked, leg], sum + 1 / leg.odds);
  };
  walk(0, [], 0);
  return covers.sort((a, b) => a.sum - b.sum).map((cover) => cover.legs);
}

function capOf(shares: readonly number[], legs: readonly ArbLeg[]): number | null {
  let cap: number | null = null;
  legs.forEach((leg, index) => {
    if (leg.limit === null || leg.limit <= 0) return;
    const total = leg.limit / shares[index];
    if (cap === null || total < cap) cap = total;
  });
  return cap;
}

export function planHold(row: LowHoldRow, event: EventHeader, chosen: readonly ArbLeg[], stake: number): HoldPlay {
  const legs = displayOrder(chosen).map((index) => chosen[index]);
  const odds = legs.map((leg) => leg.odds);
  const shares = coverShares(odds);
  const plan = planStakes({ shares, odds, limits: legs.map((leg) => leg.limit), maxStake: capOf(shares, legs), stake, middle: false }, "spend");
  return { row, event, market: marketLabel(row.market, legs), legs, hold: holdOf(odds), plan, cost: Math.max(0, -plan.profit) };
}

function firstPlaceableHold(row: LowHoldRow, event: EventHeader, covers: readonly ArbLeg[][], stake: number, maxHold: number): HoldPlay | null {
  for (const cover of covers) {
    if (holdOf(cover.map((leg) => leg.odds)) * 100 > maxHold + EPSILON) return null;
    const play = planHold(row, event, cover, stake);
    if (play.plan.placeable) return play;
  }
  return null;
}

type CachedHold = {
  readonly event: EventHeader;
  readonly stake: number;
  readonly maxHold: number;
  readonly targetBooks: readonly string[];
  readonly play: HoldPlay | null;
};
type CachedRow = { readonly selected: ReadonlySet<string>; readonly selectable: boolean; readonly hold: CachedHold | null };
const rowCache = new WeakMap<LowHoldRow, CachedRow>();

export type HoldFoldInput = {
  readonly view: LowHoldView;
  readonly filters: LowHoldFilters;
  readonly selected: ReadonlySet<string>;
  readonly hidden: ReadonlySet<string>;
  readonly search: string;
  readonly stake: number;
  readonly nowMs?: number;
};

export type HoldSource = {
  readonly ids: readonly RowId[];
  readonly selectable: number;
  readonly rowAt: (id: RowId) => HoldPlay | null;
};

export function foldLowHold({ view, filters, selected, hidden, search, stake, nowMs = Date.now() }: HoldFoldInput): HoldSource {
  const matches = searchMatcher(search);
  const startsIn = gameTimeMatcher({ days: filters.days, within: filters.within, live: false }, new Date(nowMs));
  const byId = new Map<RowId, HoldPlay>();
  const kept: HoldPlay[] = [];
  let selectable = 0;
  for (const id of view.rowIds) {
    const row = view.rows.get(id);
    const event = row === undefined ? undefined : view.events.get(row.eventId);
    if (row === undefined || event === undefined) continue;
    let cached = rowCache.get(row);
    let covers: ArbLeg[][] | null = null;
    if (cached?.selected !== selected) {
      covers = selectedCovers(row.legs, selected);
      cached = { selected, selectable: covers.length > 0, hold: null };
      rowCache.set(row, cached);
    }
    if (!cached.selectable) continue;
    selectable += 1;
    if (!matchesScope(row, filters.scope)) continue;
    if (startsIn !== null && !startsIn(event.startMs)) continue;
    if (!matchesSearch(row, event, matches) || isHidden(row, "lowhold", hidden)) continue;
    let hold = cached.hold;
    if (hold === null || hold.event !== event || hold.stake !== stake || hold.maxHold !== filters.maxHold || hold.targetBooks !== filters.targetBooks) {
      const aimed = (covers ?? selectedCovers(row.legs, selected)).filter((cover) => hitsTarget(cover, filters.targetBooks));
      hold = { event, stake, maxHold: filters.maxHold, targetBooks: filters.targetBooks, play: firstPlaceableHold(row, event, aimed, stake, filters.maxHold) };
      rowCache.set(row, { ...cached, hold });
    }
    const play = hold.play;
    if (play === null) continue;
    byId.set(id, play);
    kept.push(play);
  }
  kept.sort((a, b) => a.hold - b.hold || a.event.startMs - b.event.startMs);
  return { ids: kept.map((play) => play.row.id), selectable, rowAt: (id) => byId.get(id) ?? null };
}
